import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { userapi } from "../../services/userapi";
import { getBearerToken, setBearerToken } from "../../services/authToken";
import { writeLastLoginIdentity } from "../../utils/lastLoginIdentity";
import { suspendSseForAuthFailure } from "../../services/realtime/sseClient";
import { jwtDecode } from "jwt-decode";

// Create the context
const AuthContext = createContext();

// Custom hook for using the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Provider component
export const AuthProvider = ({ children }) => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Consolidated currentUser state
  const [currentUser, setCurrentUser] = useState(null);
  // ADDED: Loading state for initialization
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalVariant, setLoginModalVariant] = useState("session-expired");
  const [loginError, setLoginError] = useState("");
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Use a ref to store credentials in memory only (not persisted)
  // This is more secure than storing in state or localStorage
  const credentialsRef = useRef(null);

  // Logout function needs to be defined before functions that might call it (updateAuthToken, silentRefreshToken)
  const logout = useCallback(() => {
    setIsLoggingOut(true); // Set logout flag to show overlay
    try {
      credentialsRef.current = null;
      sessionStorage.clear();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setIsTokenExpired(false); // Ensure this is reset on logout
      console.log("User logged out, session cleared.");
      // Redirect to login page
      window.location.href = "/login"; // Force navigation to login page
    } finally {
      // setIsLoading(false); // Removed this line
    }
  }, [setIsLoggingOut, setIsAuthenticated, setCurrentUser, setIsTokenExpired]);

  // Helper function to decode JWT and get expiry time
  const getTokenExpiryTime = useCallback((token) => {
    try {
      const payload = jwtDecode(token);
      if (!payload || !payload.exp) return 0;
      // JWT exp is in seconds, convert to milliseconds
      return payload.exp * 1000;
    } catch (error) {
      console.error("Error decoding token:", error);
      return 0;
    }
  }, []);

  // Helper function to parse JWT payload (using jwtDecode library)
  const parseJwtPayload = (token) => {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error("Failed to parse JWT payload", error);
      return null;
    }
  };

  // Function to update auth state with a new token
  const updateAuthToken = useCallback(
    (newToken) => {
      setBearerToken(newToken);
      const payload = parseJwtPayload(newToken);

      if (payload) {
        let processedRoles = [];
        if (payload.roles && Array.isArray(payload.roles)) {
          // Transform array of role strings from JWT into array of objects {name: roleString}
          processedRoles = payload.roles.map((role) =>
            typeof role === "string" ? { name: role } : role,
          );
          // Filter out any malformed role objects (e.g., if a role in array wasn't a string initially)
          processedRoles = processedRoles.filter(
            (role) =>
              typeof role === "object" &&
              role !== null &&
              typeof role.name === "string",
          );
        }

        const username = payload.sub || sessionStorage.getItem("username");
        const name = payload.name || sessionStorage.getItem("name");
        const surname = payload.surname || sessionStorage.getItem("surname");
        const userId =
          payload.id || payload.userId || sessionStorage.getItem("userId");

        sessionStorage.setItem("username", username || "");
        sessionStorage.setItem("roles", JSON.stringify(processedRoles)); // Store transformed roles
        sessionStorage.setItem("name", name || "");
        sessionStorage.setItem("surname", surname || "");
        if (userId) {
          sessionStorage.setItem("userId", userId);
        }

        // Remember identity across logout for login prefill / future BE flows (never password).
        writeLastLoginIdentity({ userId, username });

        setCurrentUser({
          id: userId,
          username: username,
          roles: processedRoles, // Use transformed roles
          name: name,
          surname: surname,
        });
        setIsAuthenticated(true);
        setIsTokenExpired(false);
        console.log(
          "AuthContext updated with new token. Processed roles:",
          processedRoles,
        );
      } else {
        console.error("Invalid token received, cannot update auth state.");
        logout();
      }
    },
    [logout, setCurrentUser, setIsAuthenticated, setIsTokenExpired],
  );

  // Check if user is authenticated on mount
  useEffect(() => {
    setIsLoading(true);
    try {
      const token = getBearerToken();
      if (token) {
        const payload = parseJwtPayload(token);
        const expiryTime = payload ? payload.exp * 1000 : 0;
        if (expiryTime > Date.now()) {
          // If token is valid on initial load, set up the user state directly.
          // This logic is similar to parts of updateAuthToken but specific for initialization.
          const rolesArray =
            payload.roles && Array.isArray(payload.roles)
              ? payload.roles
                  .map((r) => (typeof r === "string" ? { name: r } : r))
                  .filter(
                    (r) =>
                      typeof r === "object" &&
                      r !== null &&
                      typeof r.name === "string",
                  )
              : [];
          setCurrentUser({
            id:
              payload.id || payload.userId || sessionStorage.getItem("userId"), // Prioritize JWT claims for ID
            username: payload.sub || sessionStorage.getItem("username"), // Prioritize JWT claim for username
            roles: rolesArray,
            name: payload.name || sessionStorage.getItem("name"), // Prioritize JWT claims
            surname: payload.surname || sessionStorage.getItem("surname"), // Prioritize JWT claims
          });
          setIsAuthenticated(true);
          setIsTokenExpired(false); // Ensure this is false if token is valid
        } else {
          console.log(
            "AuthContext: Token found in session on mount is expired. Clearing token.",
          );
          setBearerToken(null);
          // No need to call full logout() here, just ensure unauthenticated state for init
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } else {
        // No token found on mount
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } catch (error) {
      console.error("Error initializing auth state from session:", error);
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures it runs only once on mount

  // Note: Silent token refresh functionality was removed as proactive client-side refresh is no longer used.

  // Login function
  const login = useCallback(
    async (username, password) => {
      setIsLoading(true);
      setLoginError("");
      try {
        const loginResponse = await userapi.login(username, password);

        if (loginResponse && loginResponse.jwtToken) {
          credentialsRef.current = { username, password }; // Store credentials
          updateAuthToken(loginResponse.jwtToken); // Use the new function to set auth state

          // Collect details from loginResponse that might not have been in the JWT
          // and update both sessionStorage and currentUser state.
          const updatesForCurrentUser = {};
          const tokenPayload = parseJwtPayload(loginResponse.jwtToken);

          if (loginResponse.username && (!tokenPayload || !tokenPayload.sub)) {
            sessionStorage.setItem("username", loginResponse.username);
            updatesForCurrentUser.username = loginResponse.username;
          }
          // Roles are generally handled by updateAuthToken from JWT, but can be augmented if needed.
          // Example: if (loginResponse.roles && (!tokenPayload || !tokenPayload.roles)) { ... }

          if (loginResponse.name && (!tokenPayload || !tokenPayload.name)) {
            sessionStorage.setItem("name", loginResponse.name || "");
            updatesForCurrentUser.name = loginResponse.name || "";
          }
          if (
            loginResponse.surname &&
            (!tokenPayload || !tokenPayload.surname)
          ) {
            sessionStorage.setItem("surname", loginResponse.surname || "");
            updatesForCurrentUser.surname = loginResponse.surname || "";
          }
          if (
            loginResponse.id &&
            (!tokenPayload || (!tokenPayload.id && !tokenPayload.userId))
          ) {
            sessionStorage.setItem("userId", loginResponse.id);
            updatesForCurrentUser.id = loginResponse.id;
          }

          // If there are updates from loginResponse, apply them to currentUser state
          if (Object.keys(updatesForCurrentUser).length > 0) {
            setCurrentUser((prevUser) => ({
              ...prevUser, // Keep data set by updateAuthToken (e.g., roles from token)
              ...updatesForCurrentUser, // Override/add with direct loginResponse values
            }));
          }

          // Ensure localStorage identity includes loginResponse id/username when JWT lacked them.
          writeLastLoginIdentity({
            userId:
              updatesForCurrentUser.id ||
              loginResponse.id ||
              sessionStorage.getItem("userId"),
            username:
              updatesForCurrentUser.username ||
              loginResponse.username ||
              username ||
              sessionStorage.getItem("username"),
          });

          setShowLoginModal(false);
          setLoginModalVariant("session-expired");
          setIsLoading(false);
          return true;
        } else {
          setLoginError(
            loginResponse?.error ||
              "Login failed: Invalid response from server.",
          );
          setIsLoading(false);
          return false;
        }
      } catch (error) {
        console.error("[AuthContext] Error during login process:", error);
        if (error.message === "CREDENTIALS_EXPIRED") {
          setLoginError("CREDENTIALS_EXPIRED");
        } else {
          setLoginError(
            error.message || "Login failed. Please check your credentials.",
          );
        }
        setIsLoading(false);
        return false;
      }
    },
    [
      setIsLoading,
      setLoginError,
      updateAuthToken,
      setShowLoginModal,
      setCurrentUser,
    ],
  ); // updateAuthToken is a dependency

  // Handle token expiry triggered by axios interceptor
  const handleTokenExpiry = useCallback(async () => {
    console.log("AuthContext: handleTokenExpiry invoked by interceptor.");
    suspendSseForAuthFailure();
    setIsLoading(true);
    try {
      // Directly show login modal as silent refresh with credentials is removed
      // The backend handles proactive refresh via headers. If we reach here, it means a definitive auth failure.
      console.warn(
        "AuthContext: Token is definitively expired or invalid. Showing login modal.",
      );
      setLoginModalVariant("session-expired");
      setShowLoginModal(true);
      setIsTokenExpired(true); // Mark token as expired for UI
      setIsLoading(false);
      // It's important to reject the promise here so that the original request that triggered this
      // (via the axios interceptor) also fails, rather than trying to proceed.
      return Promise.reject(
        new Error("Token expired or invalid. User needs to re-login."),
      );
    } catch (error) {
      // This catch block might be redundant if the main logic above doesn't throw an error that needs catching here.
      // However, keeping it for safety to ensure modal is shown and state is set.
      console.error("AuthContext: Error in handleTokenExpiry:", error);
      setLoginModalVariant("session-expired");
      setShowLoginModal(true);
      setIsTokenExpired(true);
      setIsLoading(false);
      return Promise.reject(error); // Propagate error
    }
  }, [setShowLoginModal, setIsTokenExpired, setIsLoading]); // Added setIsLoading

  // Helper function to get user display name
  const getUserDisplayName = () => {
    // UPDATED: Use consolidated currentUser state
    if (currentUser?.name && currentUser?.surname) {
      return `${currentUser.name} ${currentUser.surname}`;
    }
    if (currentUser?.name) {
      // Handle case where only name might be available
      return currentUser.name;
    }
    if (currentUser?.username) {
      // Fallback to username
      return currentUser.username;
    }

    // If isLoading is true and currentUser is null or lacks specific details,
    // return an empty string to prevent "User" from flashing during transitions.
    if (isLoading) {
      return "";
    }

    return "User"; // Final fallback
  };

  const value = {
    // State
    isAuthenticated,
    currentUser,
    isLoading,
    showLoginModal,
    loginModalVariant,
    loginError,
    isTokenExpired,
    isLoggingOut,

    // Actions
    login,
    logout,
    setShowLoginModal,
    setLoginModalVariant,
    handleTokenExpiry,
    getUserDisplayName,
    updateAuthToken, // Expose updateAuthToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
